import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Avatar,
  CardHeader,
  InputAdornment,
  LinearProgress
} from "@mui/material";
import {
  Add,
  Visibility,
  Edit,
  Delete,
  MoreVert,
  Search,
  FilterList,
  Refresh,
  LocalShipping,
  Business,
  CheckCircle,
  CalendarToday,
  Inventory,
  TrendingUp,
  Receipt,
  Assignment,
  Warning
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {
  getLinenDispatches,
  getLinenVendors,
  deleteLinenDispatch,
  updateLinenDispatch
} from "../../services/api";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export const LinenDispatchList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [dispatches, setDispatches] = useState([]);
  const [filteredDispatches, setFilteredDispatches] = useState([]);
  const [vendors, setVendors] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(30, 'day'));
  const [dateTo, setDateTo] = useState(dayjs());
  
  // Dialogs and menus
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDispatch, setMenuDispatch] = useState(null);

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "dispatched", label: "Dispatched" },
    { value: "partial_received", label: "Partially Received" },
    { value: "received", label: "Received" }
  ];

  const statusColors = {
    dispatched: "warning",
    partial_received: "info",
    received: "success"
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterDispatches();
  }, [dispatches, searchTerm, statusFilter, vendorFilter, dateFrom, dateTo]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dispatchesRes, vendorsRes] = await Promise.all([
        getLinenDispatches(),
        getLinenVendors({ status: "active" })
      ]);

      setDispatches(dispatchesRes.data?.dispatches || []);
      setVendors(vendorsRes.data?.vendors || []);
    } catch (err) {
      console.error("Load data error:", err);
      enqueueSnackbar("❌ Failed to load dispatches", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const filterDispatches = () => {
    let filtered = [...dispatches];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(dispatch =>
        dispatch.dispatchNumber?.toLowerCase().includes(term) ||
        dispatch.vendor?.name?.toLowerCase().includes(term) ||
        dispatch.vendor?.vendorCode?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(dispatch => dispatch.status === statusFilter);
    }

    // Vendor filter
    if (vendorFilter !== "all") {
      filtered = filtered.filter(dispatch => dispatch.vendor?._id === vendorFilter);
    }

    // Date filter
    if (dateFrom && dateTo) {
      filtered = filtered.filter(dispatch => {
        const dispatchDate = dayjs(dispatch.pickup_time || dispatch.createdAt);
        return dispatchDate.isAfter(dateFrom.subtract(1, 'day')) && 
               dispatchDate.isBefore(dateTo.add(1, 'day'));
      });
    }

    // Sort by latest first
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setFilteredDispatches(filtered);
  };

  const handleMenuOpen = (event, dispatch) => {
    setMenuAnchor(event.currentTarget);
    setMenuDispatch(dispatch);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuDispatch(null);
  };

  const handleView = (dispatch) => {
    setSelectedDispatch(dispatch);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = (dispatch) => {
    navigate(`/linen/dispatch/edit/${dispatch._id}`);
    handleMenuClose();
  };

  const handleDeleteClick = (dispatch) => {
    setSelectedDispatch(dispatch);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDispatch) return;

    try {
      await deleteLinenDispatch(selectedDispatch._id);
      enqueueSnackbar("🗑️ Dispatch deleted successfully", { variant: "success" });
      setDeleteDialogOpen(false);
      setSelectedDispatch(null);
      loadData(); // Refresh the list
    } catch (err) {
      console.error("Delete error:", err);
      enqueueSnackbar(
        err.response?.data?.message || "❌ Failed to delete dispatch",
        { variant: "error" }
      );
    }
  };

  const handleStatusUpdate = async (dispatchId, newStatus) => {
    try {
      await updateLinenDispatch(dispatchId, { status: newStatus });
      enqueueSnackbar("✅ Status updated successfully", { variant: "success" });
      loadData(); // Refresh the list
    } catch (err) {
      console.error("Status update error:", err);
      enqueueSnackbar("❌ Failed to update status", { variant: "error" });
    }
  };

  const getTotalItems = (dispatch) => {
    return dispatch.items?.reduce((total, item) => total + (item.quantity || 0), 0) || 0;
  };

  const getTotalWeight = (dispatch) => {
    return dispatch.items?.reduce((total, item) => total + (item.weight || 0), 0) || 0;
  };

  const getSummaryStats = () => {
    const total = filteredDispatches.length;
    const dispatched = filteredDispatches.filter(d => d.status === 'dispatched').length;
    const partial = filteredDispatches.filter(d => d.status === 'partial_received').length;
    const received = filteredDispatches.filter(d => d.status === 'received').length;
    const totalItems = filteredDispatches.reduce((sum, d) => sum + getTotalItems(d), 0);

    return { total, dispatched, partial, received, totalItems };
  };

  const stats = getSummaryStats();

  if (loading && dispatches.length === 0) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: 400,
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Loading Dispatches...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Preparing dispatch information
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
        {/* Header */}
        <Card sx={{ 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 3
        }}>
          <CardContent sx={{ p: 4 }}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <LocalShipping sx={{ fontSize: 48, color: 'white' }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography 
                  variant="h2" 
                  fontWeight={800}
                  sx={{ 
                    color: 'white',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    mb: 1
                  }}
                >
                  Linen Dispatches
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'white', 
                    opacity: 0.9,
                    fontSize: '1.3rem'
                  }}
                >
                  Track and manage linen dispatches to vendors
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<Add sx={{ fontSize: 28 }} />}
                  onClick={() => navigate("/hospital/housekeeping-and-linen-management/linen/linen-dispatch")}
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    '&:hover': {
                      backgroundColor: 'grey.100',
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  New Dispatch
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <Assignment sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
                  {stats.total}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
                  Total
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
                  Dispatches
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <LocalShipping sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
                  {stats.dispatched}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
                  Dispatched
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <TrendingUp sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
                  {stats.partial}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
                  Partial
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
                  Received
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <CheckCircle sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
                  {stats.received}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
                  Received
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <Inventory sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
                  {stats.totalItems}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
                  Items
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
                  Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters Card */}
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
          <CardHeader
            title={
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
                🔍 Filter Dispatches
              </Typography>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          />
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search dispatch number, vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "primary.main", fontSize: 28 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: '1.1rem',
                      '& input': {
                        padding: '12px 14px',
                        fontSize: '1.1rem'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterList sx={{ color: 'primary.main', fontSize: 28 }} />
                      </InputAdornment>
                    }
                  >
                    {statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.1rem' }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Vendor</InputLabel>
                  <Select
                    value={vendorFilter}
                    label="Vendor"
                    onChange={(e) => setVendorFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Business sx={{ color: 'primary.main', fontSize: 28 }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="all" sx={{ fontSize: '1.1rem' }}>All Vendors</MenuItem>
                    {vendors.map(vendor => (
                      <MenuItem key={vendor._id} value={vendor._id} sx={{ fontSize: '1.1rem' }}>
                        {vendor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="From Date"
                  value={dateFrom}
                  onChange={(newValue) => setDateFrom(newValue)}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      size: "medium",
                      InputProps: {
                        sx: { fontSize: '1.1rem' },
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarToday sx={{ color: 'primary.main', fontSize: 28 }} />
                          </InputAdornment>
                        )
                      },
                      InputLabelProps: { sx: { fontSize: '1.1rem' } }
                    } 
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="To Date"
                  value={dateTo}
                  onChange={(newValue) => setDateTo(newValue)}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      size: "medium",
                      InputProps: {
                        sx: { fontSize: '1.1rem' },
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarToday sx={{ color: 'primary.main', fontSize: 28 }} />
                          </InputAdornment>
                        )
                      },
                      InputLabelProps: { sx: { fontSize: '1.1rem' } }
                    } 
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Tooltip title="Refresh Data">
                  <IconButton 
                    onClick={loadData} 
                    size="large"
                    sx={{ 
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'primary.dark' }
                    }}
                  >
                    <Refresh sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Dispatches Table */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocalShipping sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  Dispatch List
                </Typography>
                <Chip 
                  label={filteredDispatches.length} 
                  color="primary" 
                  size="medium"
                  sx={{ fontSize: '1.1rem', fontWeight: 600, minWidth: 60 }}
                />
              </Box>
            }
            action={
              (searchTerm || statusFilter !== "all" || vendorFilter !== "all") && (
                <Chip
                  label="Filters Active"
                  color="primary"
                  size="medium"
                  onDelete={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    setVendorFilter("all");
                    setDateFrom(dayjs().subtract(30, 'day'));
                    setDateTo(dayjs());
                  }}
                  sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                />
              )
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          />
          <CardContent>
            {filteredDispatches.length === 0 ? (
              <Alert 
                severity="info" 
                sx={{ 
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  '& .MuiAlert-message': { fontSize: '1.1rem' }
                }}
              >
                {dispatches.length === 0 
                  ? "📦 No dispatches found. Create your first dispatch to get started." 
                  : "🔍 No dispatches match your current filters."}
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: 'primary.main' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🏷️ Dispatch #
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🏢 Vendor
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📅 Pickup Date/Time
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📦 Items
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🔢 Quantity
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        ⚖️ Weight
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📊 Status
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📝 Created
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🛠️ Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDispatches.map((dispatch) => (
                      <TableRow 
                        key={dispatch._id} 
                        hover
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            transform: 'translateY(-1px)',
                            transition: 'all 0.3s ease'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                            {dispatch.dispatchNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {dispatch.vendor?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {dispatch.vendor?.vendorCode}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {dayjs(dispatch.pickup_time).format('DD/MM/YYYY')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {dayjs(dispatch.pickup_time).format('HH:mm')}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={dispatch.items?.length || 0}
                            size="medium"
                            variant="outlined"
                            sx={{ fontSize: '1.1rem', fontWeight: 600, minWidth: 60 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {getTotalItems(dispatch)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {getTotalWeight(dispatch).toFixed(1)} kg
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={dispatch.status}
                            color={statusColors[dispatch.status]}
                            size="medium"
                            sx={{ fontSize: '1.1rem', fontWeight: 600, minWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {dayjs(dispatch.createdAt).format('DD/MM/YYYY')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="large"
                                onClick={() => handleView(dispatch)}
                                sx={{ 
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { backgroundColor: 'primary.dark' }
                                }}
                              >
                                <Visibility sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="More Actions">
                              <IconButton
                                size="large"
                                onClick={(e) => handleMenuOpen(e, dispatch)}
                                sx={{ 
                                  backgroundColor: 'secondary.main',
                                  color: 'white',
                                  '&:hover': { backgroundColor: 'secondary.dark' }
                                }}
                              >
                                <MoreVert sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* View Dispatch Dialog */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={() => setViewDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'primary.main', 
            color: 'white',
            py: 3
          }}>
            <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
              📋 Dispatch Details - {selectedDispatch?.dispatchNumber}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            {selectedDispatch && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                    🚚 Dispatch Information
                  </Typography>
                  <Stack spacing={2}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>🏢 Vendor:</strong> {selectedDispatch.vendor?.name}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>📅 Pickup Date:</strong> {dayjs(selectedDispatch.pickup_time).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>🎒 Bag Count:</strong> {selectedDispatch.bag_count || 'Not specified'}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>📊 Status:</strong> 
                      <Chip 
                        label={selectedDispatch.status} 
                        color={statusColors[selectedDispatch.status]} 
                        size="medium" 
                        sx={{ ml: 2, fontSize: '1rem', fontWeight: 600 }}
                      />
                    </Typography>
                    {selectedDispatch.remarks && (
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        <strong>📝 Remarks:</strong> {selectedDispatch.remarks}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                    📊 Summary
                  </Typography>
                  <Stack spacing={2}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>📦 Total Items:</strong> {selectedDispatch.items?.length || 0}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>🔢 Total Quantity:</strong> {getTotalItems(selectedDispatch)}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>⚖️ Total Weight:</strong> {getTotalWeight(selectedDispatch).toFixed(2)} kg
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>📅 Created:</strong> {dayjs(selectedDispatch.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                    📦 Items
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead sx={{ backgroundColor: 'primary.main' }}>
                        <TableRow>
                          <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 2 }}>
                            Item Name
                          </TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 2 }}>
                            Code
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 2 }}>
                            Quantity
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 2 }}>
                            Weight
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDispatch.items?.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.linen?.name || 'Unknown Item'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.linen?.code || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.weight || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                {selectedDispatch.photos && selectedDispatch.photos.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                      📸 Photos ({selectedDispatch.photos.length})
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      {selectedDispatch.photos.length} photo(s) attached to this dispatch.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setViewDialogOpen(false)}
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => setDeleteDialogOpen(false)}
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'error.main', 
            color: 'white',
            py: 3
          }}>
            <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
              🗑️ Confirm Delete
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Alert severity="warning" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
              ⚠️ Are you sure you want to delete dispatch {selectedDispatch?.dispatchNumber}? 
              This action cannot be undone and will restore inventory quantities.
            </Alert>
            <Stack spacing={1}>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                <strong>🏢 Vendor:</strong> {selectedDispatch?.vendor?.name}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                <strong>📦 Items:</strong> {selectedDispatch?.items?.length}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                <strong>🔢 Total Quantity:</strong> {selectedDispatch && getTotalItems(selectedDispatch)}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button 
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                px: 4,
                py: 1
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          <MenuItem onClick={() => handleView(menuDispatch)} sx={{ fontSize: '1.1rem' }}>
            <Visibility sx={{ mr: 1, fontSize: 24 }} /> View Details
          </MenuItem>
          <MenuItem onClick={() => handleEdit(menuDispatch)} sx={{ fontSize: '1.1rem' }}>
            <Edit sx={{ mr: 1, fontSize: 24 }} /> Edit
          </MenuItem>
          {menuDispatch?.status === 'dispatched' && (
            <MenuItem onClick={() => handleStatusUpdate(menuDispatch._id, 'received')} sx={{ fontSize: '1.1rem' }}>
              <CheckCircle sx={{ mr: 1, fontSize: 24 }} /> Mark as Received
            </MenuItem>
          )}
          <MenuItem onClick={() => handleDeleteClick(menuDispatch)} sx={{ color: 'error.main', fontSize: '1.1rem' }}>
            <Delete sx={{ mr: 1, fontSize: 24 }} /> Delete
          </MenuItem>
        </Menu>
      </Box>
    </LocalizationProvider>
  );
};